/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment Name Field
 *
 * @file AssignmentNameField.jsx
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import { CircularProgress } from '@mui/material';
import Icon from '../icons/Icon';

const AssignmentNameField = ({
  name,
  control,
  label,
  classes,
  rules,
  placeholder,
  id,
  disabled = false,
  isLoading = false
}) => (
  <Controller
    name={name}
    control={control}
    rules={rules}
    render={({ field, fieldState }) => {
      const errorMessage = fieldState.error?.message;
      const hasError = Boolean(errorMessage);
      const errorId = `${id}-error`;

      return (
        <div className={classes.assignmentNameField}>
          <label htmlFor={id} className={classes.assignmentNameLabel}>
            {label}
          </label>
          {hasError && (
            <div
              id={errorId}
              className={classes.assignmentNameErrorText}
              role="alert"
            >
              <Icon name="InlineWarningIcon" className={classes.assignmentNameErrorIcon} />
              {errorMessage}
            </div>
          )}
          <div className={classes.assignmentNameInputWrap}>
            <input
              {...field}
              id={id}
              type="text"
              value={field.value || ''}
              placeholder={placeholder}
              disabled={disabled || isLoading}
              className={
                hasError
                  ? `${classes.assignmentNameInput} ${classes.assignmentNameInputError}`
                  : classes.assignmentNameInput
              }
              aria-invalid={hasError}
              aria-describedby={hasError ? errorId : undefined}
              aria-busy={isLoading}
            />
            {isLoading && (
              <CircularProgress
                size={20}
                className={classes.assignmentNameLoader}
                aria-hidden="true"
              />
            )}
          </div>
        </div>
      );
    }}
  />
);

AssignmentNameField.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  rules: PropTypes.object,
  placeholder: PropTypes.string,
  id: PropTypes.string,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool
};

AssignmentNameField.defaultProps = {
  rules: undefined,
  placeholder: '',
  id: 'assignment-form-assignment-name',
  disabled: false,
  isLoading: false
};

export default AssignmentNameField;
